import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import TopUrgencyBar from "@/components/TopUrgencyBar";
import Footer from "@/components/Footer";
import Countdown from "@/components/Countdown";
import TicketsBlock from "@/components/TicketsBlock";
import ScheduleModal from "@/components/ScheduleModal";
import MobileStickyCta from "@/components/MobileStickyCta";
import useReveal from "@/hooks/useReveal";
import {
  Briefcase, Megaphone, Waves, Users, CheckCircle2, Sparkles, MapPin, ArrowRight, Zap, Lock, Star, Mic2, ExternalLink, Instagram, Calendar, Coffee, Utensils, Compass, MessageSquare, Sun, User2, HelpCircle,
} from "lucide-react";

const SPEAKERS = [
  {
    slug: "jacob-siegel",
    name: "Jacob Siegel",
    role: "SSU Founder",
    topic: "Marketing & Social Media",
    img: "/speakers/jacob-siegel.jpeg",
    bio: "Founder of Superhero Swim University. Built a multi-six-figure swim school from scratch using social media, content, and a relentless focus on serving families.",
    accent: "#FACC15",
    objectPosition: "center",
  },
  {
    slug: "eric-lupton",
    name: "Eric Lupton",
    role: "President, Life Saver Pool Fence",
    topic: "Pool Safety & The Business Behind It",
    img: "/speakers/eric-lupton.jpg",
    bio: "Eric leads the world's largest pool fence company — a brand built around eradicating childhood drowning. He brings a one-of-a-kind perspective on water safety, brand building, and giving back.",
    accent: "#0066FF",
    objectPosition: "center",
    presentedBy: {
      name: "Life Saver Pool Fence",
      href: "https://poolfence.com",
    },
  },
  {
    slug: "shalene-hill",
    name: "Shalene Hill",
    role: "Swim Educator",
    topic: "Nervous System–Friendly Instruction",
    img: "/speakers/shalene-hill.jpeg",
    bio: "Shalene teaches instructors how to read the nervous system in the pool — turning anxious, hesitant swimmers into confident learners through trauma-informed, regulation-first methods.",
    accent: "#00B4D8",
    objectPosition: "center",
    instagram: { handle: "@singingswimschool", href: "https://www.instagram.com/singingswimschool/" },
  },
  {
    slug: "natalie-pantaleon",
    name: "Natalie Pantaleon",
    role: "Swim Coach & School Owner",
    topic: "Regulated Swimmers, Confident Learners",
    img: "/speakers/natalie-pantaleon.jpeg",
    bio: "Natalie has built her swim school around a single idea: regulation comes before instruction. She'll show you how to translate that philosophy into daily lessons that retain families for years.",
    accent: "#FACC15",
    objectPosition: "center",
    instagram: { handle: "@amazingswimmersllc", href: "https://www.instagram.com/amazingswimmersllc/" },
  },
  {
    slug: "tommy-floyd",
    name: "Tommy Floyd",
    role: "Founder, Systema Floyd",
    topic: "Built From The Ground Up · 7-Figure Operator",
    img: "/speakers/tommy-floyd.jpg",
    bio: "Tom built Systema Floyd from one classroom into a 7-figure business operating across 60+ partner schools in Florida, Georgia, and Virginia. A lifelong martial artist with 30 years of experience and an internationally-recognized curriculum, Tom is the rare operator who's done it from scratch — and serves every age from 2.5 to adult. The blueprint he'll share applies to any owner ready to scale.",
    accent: "#FACC15",
    objectPosition: "center",
    instagram: { handle: "@systema_floyd", href: "https://www.instagram.com/systema_floyd/" },
    presentedBy: {
      name: "Systema Floyd",
      href: "https://systemafloyd.com",
    },
  },
];

const PANEL_SLOTS = [
  {
    slug: "megan-wilson",
    label: "Panelist 1",
    name: "Megan Wilson",
    title: "The Neighborhood Mermaid",
    img: "/panel/megan-wilson.jpeg",
    objectPosition: "center 30%",
    bio: "A mom once called her The Neighborhood Mermaid — and the name stuck. Megan teaches more than strokes; she teaches water safety, confidence, and a genuine love for the water. Raised camping the Ten Thousand Islands of the Everglades — boating, lobstering, backcountry fishing — she's been a water baby her whole life. Bringing it from the pool deck straight to the panel.",
    instagram: {
      handle: "@theneighborhoodmermaid",
      href: "https://www.instagram.com/theneighborhoodmermaid",
    },
  },
  {
    slug: "michelle-ring",
    label: "Panelist 2",
    name: "Michele Ring",
    title: "Sea Life Swim · 40 Years Teaching",
    img: "/panel/michelle-ring.jpeg",
    objectPosition: "center 25%",
    bio: "Founder of the former All Ages Swim School in NY. 40 years teaching, 38 years with Swim-Float-Swim. Now opening a new school in North Carolina. Expect songs, parents in the water, and the kind of presence that calms a nervous kid in one breath.",
    instagram: {
      handle: "@sealifeswim",
      href: "https://www.instagram.com/sealifeswim",
    },
  },
  {
    slug: "windy-cox",
    label: "Panelist 3",
    name: "Wendy Cox",
    title: "Lowcountry Swim School",
    img: "/panel/windy-cox.jpeg",
    objectPosition: "center 25%",
    bio: "Owner of Lowcountry Swim School. A Southern hospitality energy in the water — Wendy runs a thriving school in the SC Lowcountry and knows how to build a business that families come back to year after year.",
    instagram: {
      handle: "@lowcountryswimschool",
      href: "https://www.instagram.com/lowcountryswimschool",
    },
  },
  {
    slug: "shauna-nickerson",
    label: "Panelist 4",
    name: "Shauna Nickerson",
    title: "The Swim Clinic · Owner",
    img: "/panel/shauna-nickerson.jpeg",
    objectPosition: "center 30%",
    bio: "Built The Swim Clinic from the ground up — a thriving indoor swim school focused on survival skills and private lessons. Shauna knows exactly what it takes to turn one-on-one instruction into a real, scalable business.",
    instagram: {
      handle: "@the_swim_clinic",
      href: "https://www.instagram.com/the_swim_clinic",
    },
  },
  {
    slug: "laura-romero",
    label: "Panelist 5",
    name: "Laura Romero",
    title: "Manta Splash Swim · Miami",
    img: "/panel/laura-romero.jpeg",
    objectPosition: "center 25%",
    bio: "Owner of Manta Splash Swim School in Miami. A young founder building her school in one of the country's toughest, most competitive swim markets — proof that fresh energy and a clear vision can carve out real space fast.",
    instagram: {
      handle: "@mantasplashswim",
      href: "https://www.instagram.com/mantasplashswim",
    },
  },
  {
    slug: "alex-granda",
    label: "Panelist 6",
    name: "Alex Granda",
    title: "Boca Aquatics · Boca Raton",
    img: "/panel/alex-granda.jpeg",
    objectPosition: "center 20%",
    bio: "Boca Aquatics (Swim with Alex) — swim instruction for all ages, specializing in infants and toddlers. In the water since 2005 with a gentle, playful, and happy teaching style that keeps families coming back year after year.",
    instagram: {
      handle: "@bocaaquatics",
      href: "https://www.instagram.com/bocaaquatics",
    },
  },
];

const TICKET_OPEN_AT = "2026-07-01T12:00:00-04:00"; // July 1, 2026 · 12:00 PM ET (noon)
const EVENT_START_AT = "2026-10-01T15:00:00-04:00"; // Oct 1, 2026 · 3:00 PM ET
const WHATSAPP_URL = "https://chat.whatsapp.com/HSRkuBWMRztKlJYytthG6u";

const LEARN_CARDS = [
  { icon: Briefcase, kicker: "Business & Operations", title: "Run It Like a Business", accent: "#FACC15",
    body: "Learn how to structure your swim school for growth — pricing, systems, policies, hiring, and the fundamentals that separate struggling instructors from thriving school owners." },
  { icon: Megaphone, kicker: "Marketing & Client Acquisition", title: "Fill Your Roster", accent: "#0066FF",
    body: "From social media and word-of-mouth to local SEO and referrals — walk away with a marketing strategy you can actually execute to keep your calendar full year-round." },
  { icon: Waves, kicker: "In-Pool Skills", title: "Elevate Your Teaching", accent: "#00B4D8",
    body: "Sharpen your instructional technique, curriculum design, and student progression systems. The best swim businesses are built on instructors who are exceptional in the water." },
  { icon: Users, kicker: "Peer Learning", title: "Learn from People Doing It", accent: "#FACC15",
    body: "Successful swim business owners don't learn from textbooks — they learn from each other. The conversations in the hallway are worth the ticket price alone." },
];

const INCLUDED = [
  "3 nights at the estate · October 1–4, 2026",
  "Private estate venue in West Palm Beach — spacious, vacation-style property",
  "4 expert presentation sessions — 2 in-water + 2 out-of-water",
  "Live panel + open Q&A with successful local swim business owners",
  "Mentor lunch — one-on-one time with panel members the next day",
  "Welcome reception + pool night on arrival day",
  "Catered lunches + group dinner at the estate",
  "Optional Saturday outing exploring the Palm Beach area",
  "Maximum 30 attendees — no crowds, real connections",
  "Curated swag bag with sponsor products",
  "Two workshops offered per year",
];

const SCHEDULE_DAYS = [
  {
    label: "Thursday",
    date: "Oct 1",
    accent: "#FACC15",
    tag: "Arrival Day",
    headline: "Arrive, settle in, and meet the room.",
    sessions: [
      { time: "Afternoon", title: "Arrive and Check In", desc: "Settle into the estate, get to know the space, and meet your fellow instructors.", kind: "Check-In" },
      { time: "Evening", title: "Pool, Relax, Connect", desc: "Informal welcome. Enjoy the estate, meet everyone, and get ready for a great weekend.", kind: "Social" },
    ],
  },
  {
    label: "Friday",
    date: "Oct 2",
    accent: "#0066FF",
    tag: "Full Day of Programming",
    headline: "Two presentations. One real panel. One unforgettable dinner.",
    sessions: [
      { time: "9:30 – 10:30 AM", title: "Presentation 1 — Business & Growth", desc: "Out of water. A deep dive into swim school business strategy, operations, and growth.", kind: "Out of Water" },
      { time: "11:00 AM – 12:30 PM", title: "Presentation 2 — In Water Training", desc: "Hands-on in-water session led by industry professionals. Practical techniques and methods.", kind: "In Water" },
      { time: "1:00 – 2:00 PM", title: "Lunch", desc: "Catered lunch served at the estate. Great food, great conversations.", kind: "Catered" },
      { time: "3:00 – 4:00 PM", title: "Panel Discussion — Small Business Owners", desc: "Hear directly from successful local business owners. Open Q&A — ask anything.", kind: "Open Q&A" },
      { time: "Before 7:00 PM", title: "Free Time", desc: "Relax, explore the estate, enjoy the pool, and get ready for the evening.", kind: "Free Time" },
      { time: "7:00 PM", title: "Catered Dinner at the Estate", desc: "A beautiful evening dinner with the whole group. Good food, great people, unforgettable conversations.", kind: "Included" },
    ],
  },
  {
    label: "Saturday",
    date: "Oct 3",
    accent: "#00B4D8",
    tag: "Half Day + Afternoon Adventure",
    headline: "Sharpen the craft. Then explore Palm Beach.",
    sessions: [
      { time: "9:30 – 10:30 AM", title: "Presentation 3 — In Water Training", desc: "Second in-water session. Technique, teaching methods, and real-world application.", kind: "In Water" },
      { time: "11:00 AM – 12:30 PM", title: "Presentation 4 — Business & Instruction", desc: "Final presentation session. Out of water, covering curriculum, retention, and instructor development.", kind: "Out of Water" },
      { time: "1:00 – 2:30 PM", title: "Lunch and Mentor Sessions", desc: "Panel members return as personal mentors. Sit down, ask questions one on one, and leave with real answers.", kind: "Mentors Available" },
      { time: "3:00 PM", title: "Optional Group Outing — Palm Beach Area", desc: "Join us for a fun afternoon out in Palm Beach County. Or stay at the estate and enjoy the pool. Your call.", kind: "Optional" },
    ],
  },
  {
    label: "Sunday",
    date: "Oct 4",
    accent: "#A16207",
    tag: "Checkout Day",
    headline: "New skills. New connections. Whole lot of inspiration.",
    sessions: [
      { time: "Morning", title: "Checkout and Farewells", desc: "Head home with new skills, new connections, and a whole lot of inspiration. See you next year.", kind: "Farewell" },
    ],
  },
];

export default function Landing() {
  const ref = useReveal();
  const [ticketsOpen, setTicketsOpen] = useState(() => Date.now() >= +new Date(TICKET_OPEN_AT));

  useEffect(() => {
    if (ticketsOpen) return;
    const id = setInterval(() => {
      if (Date.now() >= +new Date(TICKET_OPEN_AT)) {
        setTicketsOpen(true);
      }
    }, 15000);
    return () => clearInterval(id);
  }, [ticketsOpen]);

  const scrollToInterest = () => {
    // Interest section is retired — send them to the ticket picker area instead.
    const el = document.getElementById("tickets");
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else window.location.href = "/register";
  };

  const scrollToMembers = () => {
    const el = document.getElementById("members");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const applyToPanel = () => {
    // Panel application is now part of the /register form. Send them there
    // with the panel flag pre-set.
    window.location.href = "/register?panel=1";
  };

  return (
    <div ref={ref} className="bg-[#FAFAF7]">
      <Navbar onSaveSpot={scrollToInterest} />
      <TopUrgencyBar />

      {/* HERO */}
      <header className="relative bg-[#0A1128] text-white overflow-hidden pt-[124px] md:pt-[128px]">
        <div className="wave-bg" />
        <div className="particles absolute inset-0" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A1128]/40 to-[#0A1128]" />

        {/* Floating yellow energy splashes */}
        <div className="absolute top-32 right-[12%] w-2 h-2 rounded-full bg-[#FACC15] animate-pulse hidden lg:block" />
        <div className="absolute top-1/2 left-[8%] w-3 h-3 rounded-full bg-[#FACC15]/60 animate-pulse hidden lg:block" style={{ animationDelay: "0.8s" }} />
        <div className="absolute bottom-32 right-[40%] w-1.5 h-1.5 rounded-full bg-[#00B4D8] animate-pulse hidden lg:block" style={{ animationDelay: "1.4s" }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-10 pb-14 lg:pt-16 lg:pb-20">
          {/* Top title block */}
          <div className="text-center mb-10 lg:mb-14 reveal">
            <div className="inline-flex flex-wrap justify-center items-center gap-2 mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#FACC15]/15 border border-[#FACC15]/30 rounded-full backdrop-blur-sm">
                <Zap size={14} className="text-[#FACC15]" />
                <span className="text-[11px] font-black tracking-[0.2em] text-[#FACC15] uppercase">Oct 1–4, 2026 · West Palm Beach · Members Only</span>
              </span>
            </div>
            <h1 className="font-display text-white leading-[0.85] tracking-tight">
              <span className="block text-[11vw] sm:text-7xl md:text-8xl lg:text-9xl xl:text-[10rem]">SSU</span>
              <span className="block ssu-mark text-[11vw] sm:text-7xl md:text-8xl lg:text-9xl xl:text-[10rem]">WORKSHOPS</span>
            </h1>
            <p className="font-display-tight text-xl md:text-2xl lg:text-3xl text-white/85 mt-6 max-w-3xl mx-auto">
              The summer camp for swim <span className="text-[#FACC15] italic">instructors.</span>
            </p>
          </div>

          {/* BIG group photo */}
          <div className="relative reveal max-w-6xl mx-auto mb-12 lg:mb-16">
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#FACC15]/25 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-[#0066FF]/40 rounded-full blur-3xl pointer-events-none" />
            <div className="relative rounded-3xl overflow-hidden border-2 border-[#FACC15]/40 shadow-[0_30px_80px_-15px_rgba(0,0,0,0.6)]">
              <div className="relative aspect-[16/10] md:aspect-[2/1]">
                <img
                  src="/photos/group-celebration.jpeg"
                  alt="SSU instructors celebrating together in the pool at a past workshop"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128] via-[#0A1128]/40 to-transparent" />
                <div className="absolute top-4 right-4 md:top-6 md:right-6">
                  <div className="bg-[#FACC15] text-[#0A1128] font-black text-[10px] md:text-xs uppercase tracking-widest px-3 py-2 rounded-full inline-flex items-center gap-1.5">
                    <Star size={12} fill="#0A1128" /> 30 Seats Only
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5 md:p-10">
                  <div className="max-w-3xl">
                    <div className="text-[10px] md:text-xs font-black tracking-[0.3em] text-[#FACC15] uppercase mb-3">The Workshop Made for</div>
                    <h2 className="font-display text-3xl md:text-5xl lg:text-6xl text-white leading-[0.95]">
                      Swim Instructors Who<br />
                      <span className="ssu-mark">Mean Business.</span>
                    </h2>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Description + CTAs */}
          <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-8 lg:gap-12 items-center reveal">
            <p className="lg:col-span-7 text-lg md:text-xl text-white/80 leading-relaxed">
              <span className="font-black text-[#FACC15]">SSU Workshops</span> are 3-night in-person experiences for independent swim instructors and swim school owners. <span className="font-bold text-white">2 full days of in-water + out-of-water sessions. A live panel with local business owners. One-on-one mentor lunches. And an optional Palm Beach outing.</span> All hosted at a private estate. Whether you just started or you&apos;ve been running a swim school for years — this was built for you.
            </p>
            <div className="lg:col-span-5 flex flex-col sm:flex-row gap-3 lg:justify-end">
              <button
                onClick={scrollToInterest}
                data-testid="hero-join-interest"
                className="btn-shine bg-[#FACC15] hover:bg-[#EAB308] text-[#0A1128] font-black uppercase tracking-wider text-sm px-7 py-4 rounded-md inline-flex items-center justify-center gap-2 transition-colors shadow-[0_0_40px_rgba(250,204,21,0.3)]"
              >
                Get Your Tickets <ArrowRight size={16} />
              </button>
              <button
                onClick={scrollToMembers}
                data-testid="hero-members"
                className="text-sm font-bold text-white/90 hover:text-[#FACC15] border border-white/20 hover:border-[#FACC15]/60 uppercase tracking-wider px-6 py-4 rounded-md transition-colors"
              >
                What's a Member?
              </button>
            </div>
          </div>

          {/* TICKET LAUNCH COUNTDOWN — pre-launch teaser only (hidden once sales open) */}
          {!ticketsOpen && (
          <div className="mt-12 max-w-3xl mx-auto reveal" data-testid="hero-ticket-countdown-block">
            <div className="relative bg-gradient-to-br from-[#FACC15]/15 via-white/[0.04] to-[#0066FF]/15 border border-[#FACC15]/40 rounded-3xl p-6 md:p-8 backdrop-blur-sm">
              <div className="flex flex-col items-center text-center">
                <div className="inline-flex items-center gap-2 mb-3">
                  <span className="w-2 h-2 rounded-full bg-[#FACC15] animate-pulse" />
                  <span className="text-[10px] sm:text-xs font-black tracking-[0.3em] text-[#FACC15] uppercase">Tickets Open July 1, 2026</span>
                </div>
                <div className="font-display text-2xl sm:text-3xl md:text-4xl text-white mb-5 leading-tight">
                  Save the <span className="ssu-mark">Date.</span>
                </div>
                <Countdown target={TICKET_OPEN_AT} testIdPrefix="ticket-countdown" />
              </div>

              {/* The two gates */}
              <div className="mt-6 pt-6 border-t border-white/15">
                <p className="text-center text-xs sm:text-sm font-black uppercase tracking-[0.25em] text-[#FACC15] mb-4">
                  Two Gates Before You Can Buy
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={scrollToInterest}
                    data-testid="gate-1-interest"
                    className="group flex items-center gap-3 text-left bg-white/[0.04] hover:bg-white/[0.08] border border-white/15 hover:border-[#FACC15]/60 rounded-xl p-4 transition-colors"
                  >
                    <div className="shrink-0 w-9 h-9 rounded-lg bg-[#FACC15] text-[#0A1128] flex items-center justify-center font-display text-lg">1</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#FACC15] mb-0.5">Required</div>
                      <div className="font-display-tight text-base text-white leading-tight">Pre-register on the Interest List</div>
                    </div>
                    <ArrowRight size={16} className="text-white/40 group-hover:text-[#FACC15] shrink-0" />
                  </button>

                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="gate-2-whatsapp"
                    className="group flex items-center gap-3 bg-white/[0.04] hover:bg-[#25D366]/15 border border-white/15 hover:border-[#25D366]/60 rounded-xl p-4 transition-colors"
                  >
                    <div className="shrink-0 w-9 h-9 rounded-lg bg-[#25D366] text-white flex items-center justify-center font-display text-lg">2</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#25D366] mb-0.5">Required</div>
                      <div className="font-display-tight text-base text-white leading-tight">Join the SSU WhatsApp Group</div>
                    </div>
                    <ArrowRight size={16} className="text-white/40 group-hover:text-[#25D366] shrink-0" />
                  </a>
                </div>
                <p className="text-center text-[11px] text-white/55 mt-4">
                  Pricing and the buy link will be released to the interest list + WhatsApp first. Both gates required to access the ticket sale.
                </p>
              </div>
            </div>
          </div>
          )}
        </div>
      </header>

      {/* WHAT YOU'LL LEARN */}
      <section className="bg-[#FAFAF7] py-20 lg:py-32 relative overflow-hidden">
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#FACC15]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#0066FF]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-14 reveal">
            <div className="inline-flex items-center gap-2 mb-4">
              <Zap size={14} className="text-[#FACC15]" fill="#FACC15" />
              <span className="text-xs font-black tracking-[0.25em] text-[#0066FF] uppercase">Curriculum</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#0A1128] mb-5">
              Three Nights That Can <span className="relative inline-block">
                <span className="relative z-10">Change</span>
                <span className="absolute inset-x-0 bottom-1 h-4 bg-[#FACC15] -z-0" />
              </span> Your Swim Business.
            </h2>
            <p className="text-lg text-[#4B5563] max-w-2xl">
              Every session is built around what actually moves the needle for independent swim instructors — not theory. Real business owners. Real results.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {LEARN_CARDS.map((c, i) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.title}
                  data-testid={`learn-card-${i}`}
                  className="reveal group bg-white border border-[#E5E7EB] rounded-2xl p-8 hover:border-[#FACC15] hover:-translate-y-1 hover:shadow-xl transition-all duration-300 relative overflow-hidden"
                  style={{ transitionDelay: `${i * 50}ms` }}
                >
                  <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-0 group-hover:opacity-10 transition-opacity" style={{ background: c.accent }} />
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-6 transition-colors" style={{ backgroundColor: `${c.accent}1A`, color: c.accent }}>
                    <Icon size={22} />
                  </div>
                  <div className="text-xs font-black tracking-widest uppercase mb-2" style={{ color: c.accent === "#FACC15" ? "#A16207" : c.accent }}>{c.kicker}</div>
                  <h3 className="font-display-tight text-2xl text-[#0A1128] mb-3">{c.title}</h3>
                  <p className="text-[#4B5563] leading-relaxed">{c.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MEMBERS ONLY (NEW SECTION) */}
      <section id="members" className="bg-[#0A1128] text-white py-20 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 wave-bg opacity-50" />
        <div className="absolute top-1/4 right-10 w-72 h-72 bg-[#FACC15]/20 rounded-full blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 reveal">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FACC15]/15 border border-[#FACC15]/30 rounded-full mb-6">
              <Lock size={14} className="text-[#FACC15]" />
              <span className="text-[11px] font-black tracking-[0.25em] text-[#FACC15] uppercase">Exclusive Access</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mb-6">
              SSU Workshops Are<br />
              <span className="text-[#FACC15]">Members Only.</span>
            </h2>
            <p className="text-lg text-white/75 leading-relaxed max-w-2xl mb-5">
              Workshops are reserved for active members of the <span className="font-black text-white">Superhero Swim University</span> community. We've built a tight-knit group of instructors and school owners who push each other to be better — and the workshops are where that community comes to life in person.
            </p>
            <p className="text-base text-white/65 leading-relaxed max-w-2xl">
              Not a member yet? No problem. Start a <span className="font-black text-[#FACC15]">7-day free trial</span> of the SSU community. Explore the trainings, meet the instructors, see what we're about. If it clicks, you're in — and your workshop spot is unlocked.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href="https://www.skool.com/superheroswimuniversity/about"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="members-free-trial"
                className="btn-shine bg-[#FACC15] hover:bg-[#EAB308] text-[#0A1128] font-black uppercase tracking-wider text-sm px-8 py-4 rounded-md inline-flex items-center justify-center gap-2 transition-colors shadow-[0_0_40px_rgba(250,204,21,0.3)]"
              >
                Start My 7-Day Free Trial <ArrowRight size={16} />
              </a>
              <button
                onClick={scrollToInterest}
                data-testid="members-interest"
                className="text-sm font-bold text-white/90 hover:text-[#FACC15] border border-white/20 hover:border-[#FACC15]/60 uppercase tracking-wider px-6 py-4 rounded-md transition-colors"
              >
                I&apos;m Already a Member · Reserve My Spot
              </button>
            </div>
          </div>

          <div className="lg:col-span-5 reveal">
            <div className="bg-white/[0.04] backdrop-blur-sm border border-[#FACC15]/30 rounded-2xl p-8 relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-[#FACC15] text-[#0A1128] flex items-center justify-center font-black text-xl">
                <Star size={20} fill="#0A1128" />
              </div>
              <div className="font-display text-sm uppercase tracking-widest text-[#FACC15] mb-5 mt-2">Why Members Only</div>
              <ul className="space-y-4">
                {[
                  "30 seats. No noise. Only instructors who are all-in.",
                  "Shared vocabulary, shared standards, shared community.",
                  "Year-round relationships — not a one-time event.",
                  "Access to recordings, replays & ongoing community.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-white/90">
                    <CheckCircle2 size={18} className="text-[#FACC15] shrink-0 mt-0.5" />
                    <span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="bg-[#FAFAF7] py-20 lg:py-32 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-14 reveal">
            <div className="inline-flex items-center gap-2 mb-4">
              <Users size={14} className="text-[#0066FF]" />
              <span className="text-xs font-black tracking-[0.25em] text-[#0066FF] uppercase">Who It's For</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#0A1128] mb-5">
              Built for Every Stage of the Journey.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div data-testid="audience-new" className="reveal bg-white border-2 border-[#E5E7EB] rounded-2xl p-10 hover:border-[#0066FF] hover:-translate-y-1 transition-all relative overflow-hidden">
              <div className="absolute top-4 right-4 w-12 h-12 rounded-full bg-[#0066FF]/10 flex items-center justify-center">
                <span className="font-display text-[#0066FF] text-lg">A</span>
              </div>
              <div className="font-display text-sm uppercase tracking-widest text-[#0066FF] mb-4">Profile A</div>
              <h3 className="font-display-tight text-3xl text-[#0A1128] mb-5">Brand New Instructors</h3>
              <p className="text-[#4B5563] leading-relaxed">
                You just started (or are thinking about starting) your swim teaching business. You're not sure what to charge, how to market yourself, or how to build something sustainable. This workshop gives you the roadmap — and the community — to get there faster.
              </p>
            </div>
            <div data-testid="audience-established" className="reveal bg-white border-2 border-[#E5E7EB] rounded-2xl p-10 hover:border-[#FACC15] hover:-translate-y-1 transition-all relative overflow-hidden">
              <div className="absolute top-4 right-4 w-12 h-12 rounded-full bg-[#FACC15]/15 flex items-center justify-center">
                <span className="font-display text-[#A16207] text-lg">B</span>
              </div>
              <div className="font-display text-sm uppercase tracking-widest text-[#A16207] mb-4">Profile B</div>
              <h3 className="font-display-tight text-3xl text-[#0A1128] mb-5">Established School Owners</h3>
              <p className="text-[#4B5563] leading-relaxed">
                You've been running your swim school for years. Business is good, but you're ready to grow — more students, better systems, maybe even a team. You'll leave with fresh perspective, proven strategies from peers, and relationships that last.
              </p>
            </div>
          </div>
          <p className="text-center mt-12 font-display-tight text-2xl text-[#0A1128] reveal">
            Wherever you are in your journey, <span className="text-[#0066FF]">you belong here.</span>
          </p>
        </div>
      </section>

      {/* THE ESTATE */}
      <section id="estate" className="relative bg-[#FAFAF7] py-20 lg:py-32 overflow-hidden">
        <div className="absolute top-20 right-0 w-96 h-96 bg-[#FACC15]/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#00B4D8]/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-8 mb-12 items-end">
            <div className="lg:col-span-7 reveal">
              <div className="inline-flex items-center gap-2 mb-4">
                <MapPin size={14} className="text-[#0066FF]" />
                <span className="text-xs font-black tracking-[0.25em] text-[#0066FF] uppercase">The Estate · Locked In</span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#0A1128] leading-[0.95] mb-5">
                We Just Booked The <span className="relative inline-block">
                  <span className="relative z-10">Most Insane</span>
                  <span className="absolute inset-x-0 bottom-1 h-4 bg-[#FACC15] -z-0" />
                </span> Venue In South Florida.
              </h2>
              <p className="text-lg text-[#4B5563] max-w-2xl">
                A private Spanish-style estate in West Palm Beach. <span className="font-black text-[#0A1128]">A 30+ foot screened pool</span> for in-water sessions. A poolside lounge and bar, a covered game pavilion with a built-in fireplace + grill, a parkour ninja area — and the kind of grounds that turn a workshop into a memory.
              </p>
              <a
                href="https://www.google.com/maps/search/?api=1&query=The+Grand+Hacienda+Palm+Beach+8028+Pioneer+Road+West+Palm+Beach+FL+33405"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="estate-address-link"
                className="group inline-flex items-start gap-3 mt-6 bg-white border-2 border-[#0A1128]/10 hover:border-[#0066FF]/50 rounded-2xl px-5 py-4 transition-colors"
              >
                <MapPin size={20} className="text-[#0066FF] mt-0.5 flex-shrink-0" />
                <div className="text-left">
                  <div className="text-[10px] font-black uppercase tracking-[0.25em] text-[#0066FF] mb-0.5">The Venue</div>
                  <div className="font-display-tight text-lg text-[#0A1128] leading-tight">The Grand Hacienda Palm Beach</div>
                  <div className="text-sm text-[#4B5563] mt-0.5">8028 Pioneer Road · West Palm Beach, FL 33405</div>
                  <div className="text-xs text-[#0066FF] group-hover:underline mt-1 font-semibold">Open in Google Maps →</div>
                </div>
              </a>
            </div>
            <div className="lg:col-span-5 lg:text-right reveal">
              <div className="inline-flex flex-col gap-1 bg-[#0A1128] text-white px-5 py-4 rounded-2xl">
                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-[#FACC15]">Capacity</span>
                <span className="font-display-tight text-2xl">30 attendees max</span>
                <span className="text-xs text-white/60">Intentionally intimate. Every face matters.</span>
              </div>
            </div>
          </div>

          {/* Editorial gallery — hero + 4 supporting shots */}
          <div className="grid grid-cols-12 grid-rows-[260px_260px] sm:grid-rows-[320px_320px] lg:grid-rows-[420px_420px] gap-3 lg:gap-4">
            {/* HERO — screened pool */}
            <figure className="col-span-12 lg:col-span-8 row-span-2 relative rounded-2xl overflow-hidden group reveal">
              <img
                src="/estate/indoor-pool.jpeg"
                alt="The 30+ foot screened pool at the SSU Workshops estate"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                data-testid="estate-indoor-pool"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/85 via-[#0A1128]/0 to-transparent" />
              <figcaption className="absolute bottom-0 left-0 right-0 p-6 lg:p-8 text-white">
                <div className="text-[10px] font-black uppercase tracking-[0.3em] text-[#FACC15] mb-2">🏊 In-Water HQ</div>
                <div className="font-display-tight text-2xl lg:text-3xl mb-1">A 30+ Foot Screened Pool</div>
                <div className="text-sm text-white/75 max-w-md">Where the in-water training sessions happen. Climate-controlled, screened-in, lit from above — built for serious instruction.</div>
              </figcaption>
            </figure>

            {/* Front exterior */}
            <figure className="col-span-6 lg:col-span-4 row-span-1 relative rounded-2xl overflow-hidden group reveal">
              <img
                src="/estate/front-exterior.jpeg"
                alt="Spanish-style front exterior of the estate"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                data-testid="estate-front-exterior"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/80 via-transparent to-transparent" />
              <figcaption className="absolute bottom-0 left-0 right-0 p-4 lg:p-5 text-white">
                <div className="text-[9px] font-black uppercase tracking-[0.25em] text-[#FACC15] mb-0.5">🏛 The House</div>
                <div className="font-display-tight text-lg leading-tight">Spanish-Style Estate</div>
              </figcaption>
            </figure>

            {/* Pool lounge */}
            <figure className="col-span-6 lg:col-span-4 row-span-1 relative rounded-2xl overflow-hidden group reveal">
              <img
                src="/estate/pool-lounge.jpeg"
                alt="Poolside lounge area at the estate"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                data-testid="estate-pool-lounge"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/80 via-transparent to-transparent" />
              <figcaption className="absolute bottom-0 left-0 right-0 p-4 lg:p-5 text-white">
                <div className="text-[9px] font-black uppercase tracking-[0.25em] text-[#FACC15] mb-0.5">🌴 Poolside</div>
                <div className="font-display-tight text-lg leading-tight">Lounge & Bar</div>
              </figcaption>
            </figure>
          </div>

          {/* Second row of two more shots */}
          <div className="grid grid-cols-12 gap-3 lg:gap-4 mt-3 lg:mt-4">
            <figure className="col-span-12 sm:col-span-7 relative rounded-2xl overflow-hidden group reveal h-[260px] sm:h-[320px] lg:h-[380px]">
              <img
                src="/estate/game-pavilion.jpeg"
                alt="Covered game pavilion with fireplace, grill, and oversized games"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                data-testid="estate-game-pavilion"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/85 via-[#0A1128]/10 to-transparent" />
              <figcaption className="absolute bottom-0 left-0 right-0 p-5 lg:p-6 text-white">
                <div className="text-[10px] font-black uppercase tracking-[0.3em] text-[#FACC15] mb-1">🔥 Game Pavilion</div>
                <div className="font-display-tight text-xl lg:text-2xl leading-tight mb-1">Fireplace. Grill. Connect Four. Group Dinners.</div>
                <div className="text-sm text-white/75 max-w-md">Where the panel runs. Where dinner happens. Where the best conversations of the weekend will spill out of.</div>
              </figcaption>
            </figure>

            <figure className="col-span-12 sm:col-span-5 relative rounded-2xl overflow-hidden group reveal h-[260px] sm:h-[320px] lg:h-[380px]">
              <img
                src="/estate/parkour-ninja.jpeg"
                alt="Outdoor parkour ninja training area"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                data-testid="estate-parkour-ninja"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/85 via-[#0A1128]/10 to-transparent" />
              <figcaption className="absolute bottom-0 left-0 right-0 p-5 lg:p-6 text-white">
                <div className="text-[10px] font-black uppercase tracking-[0.3em] text-[#FACC15] mb-1">🥷 On the Grounds</div>
                <div className="font-display-tight text-xl lg:text-2xl leading-tight">Parkour Ninja Area</div>
                <div className="text-sm text-white/75 mt-1">Tennis court, fitness rig, room to move. Bring your competitive streak.</div>
              </figcaption>
            </figure>
          </div>

          {/* Estate highlights strip */}
          <div className="mt-10 reveal grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "Private Estate", sub: "West Palm Beach" },
              { label: "Screened Pool", sub: "30+ ft · all-weather" },
              { label: "Covered Pavilion", sub: "Panel + Dinner Venue" },
              { label: "Parkour + Tennis", sub: "Bring sneakers" },
            ].map((h) => (
              <div key={h.label} className="bg-white border border-[#E5E7EB] rounded-xl p-4">
                <div className="font-display-tight text-base text-[#0A1128] leading-tight">{h.label}</div>
                <div className="text-xs text-[#6B7280] mt-1">{h.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCHEDULE — compact trigger, full detail lives in modal */}
      <section id="schedule" className="relative bg-[#0A1128] text-white py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 wave-bg opacity-40 pointer-events-none" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-[#FACC15]/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-20 left-0 w-96 h-96 bg-[#00B4D8]/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-10 reveal">
            <div className="inline-flex items-center gap-2 mb-4">
              <Calendar size={14} className="text-[#FACC15]" />
              <span className="text-xs font-black tracking-[0.25em] text-[#FACC15] uppercase">The Schedule</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] mb-4">
              Three Nights. One <span className="ssu-mark">Estate.</span>
            </h2>
            <p className="text-base md:text-lg text-white/75 max-w-2xl mx-auto">
              <span className="font-black text-white">Two full days of content — Friday and Saturday.</span> Bookended by arrival and Sunday send-off. Every block is intentional.
            </p>
          </div>

          {/* Compact 4-column day strip + trigger */}
          <div className="relative bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-3xl p-6 md:p-8 reveal">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
              {SCHEDULE_DAYS.map((day, di) => {
                const Icon = [Sun, Briefcase, Waves, Compass][di % 4];
                return (
                  <div
                    key={day.label}
                    data-testid={`schedule-day-strip-${di + 1}`}
                    className="bg-white/[0.04] border border-white/10 rounded-xl p-3 md:p-4 hover:border-[#FACC15]/50 transition-colors"
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center mb-2"
                      style={{ backgroundColor: `${day.accent}22`, color: day.accent }}
                    >
                      <Icon size={16} />
                    </div>
                    <div className="text-[9px] font-black uppercase tracking-widest mb-1" style={{ color: day.accent }}>
                      {day.tag.split(" · ")[0]}
                    </div>
                    <div className="font-display text-xl md:text-2xl text-white leading-none">{day.label}</div>
                    <div className="text-[10px] text-white/50 uppercase tracking-wide mt-1 truncate">{day.date.split(" · ")[0]}</div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-4 border-t border-white/10">
              <p className="text-sm text-white/70 flex-1 text-center md:text-left">
                4 presentations · 1 open panel · 1 mentor lunch · 2 catered meals · pool time and estate access throughout.
              </p>
              <ScheduleModal
                days={SCHEDULE_DAYS}
                trigger={
                  <button
                    type="button"
                    data-testid="schedule-full-cta"
                    className="btn-shine inline-flex items-center gap-2 bg-[#FACC15] hover:bg-[#EAB308] text-[#0A1128] font-black uppercase tracking-wider text-xs md:text-sm px-6 py-3 rounded-md transition-colors shrink-0"
                  >
                    Open the Full 4-Day Breakdown <ArrowRight size={15} />
                  </button>
                }
              />
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="bg-white border-y border-[#E5E7EB] py-20 lg:py-32 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 reveal">
            <div className="inline-flex items-center gap-2 mb-4">
              <Sparkles size={14} className="text-[#FACC15]" fill="#FACC15" />
              <span className="text-xs font-black tracking-[0.25em] text-[#0066FF] uppercase">The Package</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-[#0A1128] mb-5">
              Here's What's Included With Your Ticket.
            </h2>
            <p className="text-[#4B5563] mb-6">Everything you need to learn, connect, and walk away inspired. Nothing extra. Nothing fluff.</p>
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] border-2 border-[#FACC15]/30">
              <img src="/photos/dinner-night.jpeg" alt="SSU instructors at group dinner under string lights" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/80 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-5 right-5 text-white">
                <div className="text-[10px] font-black uppercase tracking-widest text-[#FACC15] mb-1">Catered Meals Included</div>
                <div className="font-display-tight text-lg">"It felt like the best summer of my career."</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 reveal">
            <ul className="space-y-3">
              {INCLUDED.map((item, i) => (
                <li
                  key={item}
                  data-testid={`included-${i}`}
                  className="flex items-start gap-4 bg-[#FAFAF7] border border-[#E5E7EB] rounded-lg p-4 hover:border-[#FACC15] hover:bg-[#FFFBEB] transition-colors"
                >
                  <CheckCircle2 className="text-[#0066FF] shrink-0 mt-0.5" size={20} />
                  <span className="text-[#0A1128] font-semibold">{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs text-[#6B7280] italic">
              Lodging is not included in the ticket price — attendees book their own accommodations. Depending on the venue we land on, there may be an option to stay at the house with the group — more details coming soon.
            </p>
          </div>
        </div>
      </section>

      {/* PRICING TEASER (pre-launch) or TICKETS (post-launch) */}
      {ticketsOpen ? (
        <TicketsBlock sectionId="tickets" />
      ) : (
        <section className="bg-[#FAFAF7] py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] bg-[#FACC15]/10 blur-3xl rounded-full" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-12 text-center reveal">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#FACC15] text-[#0A1128] mb-6 rotate-6">
            <Sparkles size={28} fill="#0A1128" />
          </div>
          <h2 className="font-display text-4xl md:text-5xl text-[#0A1128] mb-5">
            Priced for the Independent Instructor.
          </h2>
          <p className="text-lg text-[#4B5563] max-w-2xl mx-auto mb-8">
            We built SSU Workshops with real swim instructors in mind — not big franchises. Tickets are intentionally affordable — meals, the panel, the mentor lunch, and the venue are all built in. <span className="font-black text-[#0A1128]">Tickets are live — grab yours before your tier sells out.</span>
          </p>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A1128] rounded-full">
              <span className="text-[10px] font-black tracking-[0.25em] text-[#FACC15] uppercase">Tickets open in</span>
              <Countdown target={TICKET_OPEN_AT} compact testIdPrefix="pricing-countdown" />
            </div>
          </div>
          <button
            onClick={scrollToInterest}
            data-testid="pricing-cta"
            className="btn-shine bg-[#0066FF] hover:bg-[#0052CC] text-white font-black uppercase tracking-wider text-sm px-8 py-4 rounded-md inline-flex items-center gap-2 transition-colors"
          >
            Get Notified When Tickets Open <ArrowRight size={16} />
          </button>
        </div>
      </section>
      )}

      {/* SPEAKERS */}
      <section id="speakers" className="bg-[#FAFAF7] py-20 lg:py-32 relative overflow-hidden">
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#FACC15]/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-10 left-0 w-96 h-96 bg-[#0066FF]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-14 reveal">
            <div className="inline-flex items-center gap-2 mb-4">
              <Mic2 size={14} className="text-[#0066FF]" />
              <span className="text-xs font-black tracking-[0.25em] text-[#0066FF] uppercase">Featured Speakers</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#0A1128] mb-5">
              The People You'll <span className="relative inline-block">
                <span className="relative z-10">Learn From.</span>
                <span className="absolute inset-x-0 bottom-1 h-4 bg-[#FACC15] -z-0" />
              </span>
            </h2>
            <p className="text-lg text-[#4B5563] max-w-2xl">
              Hand-picked experts who are actively running successful swim businesses, building safer communities, and pushing the craft of teaching forward. No fluff. No theory. Just the real thing.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {SPEAKERS.map((s, i) => (
              <article
                key={s.slug}
                data-testid={`speaker-card-${s.slug}`}
                className={`reveal group bg-white border-2 rounded-2xl overflow-hidden hover:-translate-y-1.5 hover:shadow-2xl transition-all duration-300 flex flex-col ${
                  s.silhouette
                    ? "border-dashed border-[#0066FF]/40 hover:border-[#FACC15]"
                    : "border-[#E5E7EB] hover:border-[#FACC15]"
                }`}
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="relative aspect-square overflow-hidden bg-[#0A1128]">
                  {s.silhouette ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div
                        className="absolute inset-0"
                        style={{
                          background:
                            "radial-gradient(ellipse at center, #15203F 0%, #0A1128 70%), repeating-linear-gradient(45deg, transparent 0 10px, rgba(255,255,255,0.02) 10px 11px)",
                        }}
                      />
                      <svg
                        viewBox="0 0 200 220"
                        className="relative w-3/5 h-3/5 text-white/15"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <circle cx="100" cy="72" r="42" />
                        <path d="M28 220 C28 160, 70 130, 100 130 C130 130, 172 160, 172 220 Z" />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-white/85">
                        <div className="w-12 h-12 rounded-full bg-[#FACC15]/15 border border-[#FACC15]/40 flex items-center justify-center mt-20">
                          <HelpCircle size={22} className="text-[#FACC15]" />
                        </div>
                        <div className="mt-3 text-[10px] font-black uppercase tracking-[0.3em] text-[#FACC15]">
                          To Be Announced
                        </div>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={s.img}
                      alt={`${s.name} — ${s.role}`}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      style={{ objectPosition: s.objectPosition || "center" }}
                    />
                  )}
                  <div className="absolute top-3 left-3">
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full backdrop-blur-md text-[9px] font-black uppercase tracking-widest text-[#0A1128]"
                      style={{ backgroundColor: s.accent }}
                    >
                      <Mic2 size={10} /> {s.silhouette ? "Slot 5" : "Speaker"}
                    </span>
                  </div>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="font-display-tight text-xl text-[#0A1128] mb-1 leading-tight">{s.name}</h3>
                  <div className="text-[11px] font-black uppercase tracking-widest text-[#0066FF] mb-3">{s.role}</div>
                  <div className="text-sm font-semibold text-[#0A1128] mb-3 leading-snug">
                    <span className="text-[#A16207]">Talk:</span> {s.topic}
                  </div>
                  <p className="text-sm text-[#4B5563] leading-relaxed flex-1">{s.bio}</p>

                  {s.instagram && (
                    <a
                      href={s.instagram.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`instagram-link-${s.slug}`}
                      className="mt-5 inline-flex items-center justify-between gap-2 px-3 py-2.5 bg-[#FAFAF7] hover:bg-[#0A1128] border border-[#E5E7EB] hover:border-[#0A1128] text-[#0A1128] hover:text-white rounded-lg transition-colors group/ig"
                    >
                      <span className="flex items-center gap-2.5">
                        <span
                          className="w-8 h-8 rounded-md flex items-center justify-center"
                          style={{
                            background: "linear-gradient(135deg,#F58529 0%,#DD2A7B 50%,#8134AF 100%)",
                          }}
                        >
                          <Instagram size={16} className="text-white" />
                        </span>
                        <span className="flex flex-col items-start leading-tight">
                          <span className="text-[9px] font-black uppercase tracking-[0.2em] text-[#6B7280] group-hover/ig:text-[#FACC15]">Follow on Instagram</span>
                          <span className="text-sm font-display-tight">{s.instagram.handle}</span>
                        </span>
                      </span>
                      <ExternalLink size={14} className="text-[#9CA3AF] group-hover/ig:text-white shrink-0" />
                    </a>
                  )}

                  {s.presentedBy && (
                    <a
                      href={s.presentedBy.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`sponsor-link-${s.slug}`}
                      className="mt-5 inline-flex items-center justify-between gap-2 px-3 py-2.5 bg-[#0A1128] hover:bg-[#0066FF] text-white rounded-lg transition-colors group/sponsor"
                    >
                      <span className="flex flex-col items-start leading-tight">
                        <span className="text-[9px] font-black uppercase tracking-[0.2em] text-[#FACC15]">In Partnership With</span>
                        {s.slug === "eric-lupton" ? (
                          <span className="text-sm font-display-tight">
                            <span style={{ color: "#ef6f28" }}>Life</span>{" "}
                            <span style={{ color: "#60a5fa" }}>Saver</span>{" "}
                            <span className="text-white">Pool Fence</span>
                          </span>
                        ) : (
                          <span className="text-sm font-display-tight text-white">{s.presentedBy.name}</span>
                        )}
                      </span>
                      <ExternalLink size={14} className="text-white/70 group-hover/sponsor:text-white shrink-0" />
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>

          {/* Partner / Courtesy strip */}
          <div className="mt-14 reveal space-y-4">
            <div className="bg-white border-2 border-[#E5E7EB] rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <div className="text-[10px] font-black uppercase tracking-[0.3em] text-[#0066FF] mb-2">Proud Partner</div>
                <div className="font-display-tight text-xl text-[#0A1128]">
                  Keeping little feet on solid ground since 1987.
                </div>
                <div className="text-sm text-[#6B7280] mt-1">
                  Eric Lupton's team at Life Saver Pool Fence is the world's #1 pool fence company — a perfect partner for a community of swim instructors built around water safety.
                </div>
              </div>
              <a
                href="https://poolfence.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="lspf-partner-link"
                className="shrink-0 inline-flex items-center gap-3 px-5 py-4 bg-[#0A1128] hover:bg-[#0066FF] text-white rounded-xl transition-colors group"
              >
                <span className="flex flex-col items-start leading-tight">
                  <span className="text-[9px] font-black uppercase tracking-[0.25em] text-[#FACC15]">Visit</span>
                  <span className="font-display-tight text-xl">
                    <span style={{ color: "#ef6f28" }}>Life</span>{" "}
                    <span style={{ color: "#60a5fa" }}>Saver</span>{" "}
                    <span className="text-white">Pool Fence</span>
                  </span>
                </span>
                <ExternalLink size={18} className="text-white/70 group-hover:text-white" />
              </a>
            </div>

            {/* Systema Floyd partner strip */}
            <div className="bg-white border-2 border-[#E5E7EB] rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <div className="text-[10px] font-black uppercase tracking-[0.3em] text-[#0066FF] mb-2">Proud Partner</div>
                <div className="font-display-tight text-xl text-[#0A1128]">
                  Building disciplined, confident kids since day one.
                </div>
                <div className="text-sm text-[#6B7280] mt-1">
                  Tom Floyd's team at Systema Floyd runs character-first martial arts across 60+ partner schools in Florida, Georgia, and Virginia — and serves every age from 2.5 to adult. Built from the ground up.
                </div>
              </div>
              <a
                href="https://systemafloyd.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="systema-floyd-partner-link"
                className="shrink-0 inline-flex items-center gap-4 px-5 py-4 bg-[#0A1128] hover:bg-[#1e3a8a] text-white rounded-xl transition-colors group"
              >
                <img
                  src="/sponsors/systema-floyd.png"
                  alt="Systema Floyd"
                  className="h-12 w-auto opacity-95 group-hover:opacity-100 transition-opacity"
                  style={{ filter: "brightness(0) invert(1)" }}
                />
                <span className="flex flex-col items-start leading-tight">
                  <span className="text-[9px] font-black uppercase tracking-[0.25em] text-[#FACC15]">Visit</span>
                  <span className="font-display-tight text-xl text-white">
                    Systema Floyd
                  </span>
                </span>
                <ExternalLink size={18} className="text-white/70 group-hover:text-white" />
              </a>
            </div>
          </div>

          {/* Sponsor / Partner CTA */}
          <div className="mt-14 reveal">
            <div
              data-testid="sponsor-cta-banner"
              className="relative overflow-hidden bg-[#0A1128] text-white rounded-3xl p-8 md:p-12"
            >
              <div className="absolute -top-16 -right-16 w-80 h-80 bg-[#FACC15]/15 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute -bottom-20 -left-16 w-96 h-96 bg-[#0066FF]/20 rounded-full blur-3xl pointer-events-none" />
              <div className="relative grid md:grid-cols-[1.4fr_1fr] gap-8 items-center">
                <div>
                  <div className="text-[10px] font-black tracking-[0.3em] text-[#FACC15] uppercase mb-3">
                    Want to sponsor SSU Workshops?
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.05] mb-4">
                    Get your brand in front of<br />
                    <span className="relative inline-block">
                      <span className="relative z-10">40+ swim-school owners.</span>
                      <span className="absolute inset-x-0 bottom-1 h-3 bg-[#FACC15]/90 -z-0" />
                    </span>
                  </h3>
                  <p className="text-base md:text-lg text-white/80 leading-relaxed max-w-xl">
                    Three tiers — from Title Partner (1 spot, category-exclusive) to Community Sponsor (product only, no cost). Four days on property, real conversations, no expo-hall noise.
                  </p>
                </div>
                <div className="flex flex-col gap-3">
                  <Link
                    to="/sponsor"
                    data-testid="sponsor-cta-primary"
                    className="btn-shine inline-flex items-center justify-center gap-2 bg-[#FACC15] hover:bg-[#EAB308] text-[#0A1128] font-black tracking-widest uppercase px-6 py-4 rounded-md transition-colors text-sm"
                  >
                    View sponsor tiers <ArrowRight size={16} />
                  </Link>
                  <Link
                    to="/sponsor#sponsor-form"
                    data-testid="sponsor-cta-secondary"
                    className="inline-flex items-center justify-center gap-2 border-2 border-white/25 hover:border-[#FACC15] text-white font-black tracking-widest uppercase px-6 py-4 rounded-md transition-colors text-sm"
                  >
                    Apply now
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PANEL */}
      <section id="panel" className="relative bg-[#0A1128] text-white py-20 lg:py-32 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FACC15]/40 to-transparent" />
        <div className="absolute top-20 left-10 w-80 h-80 bg-[#FACC15]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-10 right-0 w-96 h-96 bg-[#0066FF]/15 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-14 reveal">
            <div className="inline-flex items-center gap-2 mb-4">
              <Users size={14} className="text-[#FACC15]" />
              <span className="text-xs font-black tracking-[0.25em] text-[#FACC15] uppercase">The Panel · Day 2</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mb-5 leading-[0.95]">
              Six Local Business Owners.<br />
              <span className="relative inline-block">
                <span className="relative z-10">One Open Mic.</span>
                <span className="absolute inset-x-0 bottom-1 h-4 bg-[#FACC15]/90 -z-0" />
              </span>
            </h2>
            <p className="text-lg text-white/75 max-w-2xl">
              On Friday afternoon, six successful local business owners take the stage for one hour of completely open Q&A. Ask anything — marketing, pricing, hiring, scaling, what they wish they'd known. The kind of conversation you can't get from a podcast.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 reveal">
            {PANEL_SLOTS.map((p, i) => {
              const filled = Boolean(p.name);
              return (
                <div
                  key={p.slug}
                  data-testid={`panelist-card-${i + 1}`}
                  className={`group relative bg-white/[0.04] border-2 ${filled ? "border-[#FACC15]/60 hover:border-[#FACC15]" : "border-dashed border-white/15 hover:border-[#FACC15]/60"} rounded-2xl overflow-hidden transition-colors`}
                  style={{ transitionDelay: `${i * 50}ms` }}
                >
                  <div className="relative aspect-square overflow-hidden bg-[#0A1128]">
                    {filled ? (
                      <>
                        <img
                          src={p.img}
                          alt={p.name}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                          style={{ objectPosition: p.objectPosition || "center" }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/85 via-[#0A1128]/10 to-transparent" />
                      </>
                    ) : (
                      <>
                        <div
                          className="absolute inset-0"
                          style={{
                            background:
                              "radial-gradient(ellipse at center, #15203F 0%, #0A1128 70%), repeating-linear-gradient(45deg, transparent 0 8px, rgba(255,255,255,0.025) 8px 9px)",
                          }}
                        />
                        <svg
                          viewBox="0 0 200 220"
                          className="absolute inset-0 m-auto w-3/5 h-3/5 text-white/15"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <circle cx="100" cy="72" r="42" />
                          <path d="M28 220 C28 160, 70 130, 100 130 C130 130, 172 160, 172 220 Z" />
                        </svg>
                      </>
                    )}
                    <div className="absolute top-2 left-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#FACC15] text-[#0A1128] rounded-full text-[8px] font-black uppercase tracking-widest">
                        <Users size={9} /> {filled ? "Confirmed" : "Panel"}
                      </span>
                    </div>
                  </div>
                  <div className="p-3 text-center">
                    {filled ? (
                      <>
                        <div className="font-display-tight text-base text-white leading-tight">{p.name}</div>
                        {p.title && (
                          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#FACC15] mt-1">{p.title}</div>
                        )}
                        {p.bio && (
                          <p className="text-[11px] text-white/70 leading-snug mt-2">{p.bio}</p>
                        )}
                        {p.instagram && (
                          <a
                            href={p.instagram.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`panelist-ig-${p.slug}`}
                            className="inline-flex items-center gap-1 mt-2 text-[10px] font-black uppercase tracking-wider text-white/80 hover:text-[#FACC15] transition-colors"
                          >
                            <Instagram size={11} /> {p.instagram.handle}
                          </a>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#FACC15] mb-1">{p.label}</div>
                        <div className="text-xs font-semibold text-white/80">To Be Announced</div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTAs — Panel Confirmed + WhatsApp */}
          <div className="mt-12 reveal grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* Primary: Panel Locked In */}
            <div
              data-testid="panel-apply-cta"
              className="lg:col-span-3 bg-[#FACC15] rounded-2xl p-6 md:p-7"
            >
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-12 h-12 bg-[#0A1128] rounded-xl flex items-center justify-center">
                    <Users size={22} className="text-[#FACC15]" />
                  </div>
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A16207] mb-1">6 of 6 · Panel Locked In</div>
                    <div className="font-display-tight text-xl md:text-2xl text-[#0A1128] leading-tight">
                      The panel is full.
                    </div>
                    <div className="text-sm text-[#0A1128]/75 mt-1">
                      Applications are closed. All six seats confirmed — Megan, Michele, Wendy, Shauna, Laura, and Alex will be live on the mic Friday afternoon. Get your ticket to be in the room.
                    </div>
                  </div>
                </div>
                <Link
                  to="/register?tier=day-pass"
                  data-testid="panel-cta-get-ticket"
                  className="shrink-0 inline-flex items-center gap-2 px-5 py-3 bg-[#0A1128] text-[#FACC15] hover:bg-[#0066FF] hover:text-white font-black uppercase tracking-wider text-sm rounded-xl transition-colors"
                >
                  Get Your Ticket
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>

            {/* Secondary: WhatsApp */}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="panel-whatsapp-cta"
              className="lg:col-span-2 block bg-[#25D366]/15 hover:bg-[#25D366]/25 border-2 border-[#25D366]/40 hover:border-[#25D366] rounded-2xl p-6 transition-all group hover:-translate-y-0.5"
            >
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-12 h-12 bg-[#25D366] rounded-xl flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                    </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-black uppercase tracking-[0.25em] text-[#FACC15] mb-1">Names Drop Here First</div>
                  <div className="font-display-tight text-base md:text-lg text-white leading-tight">
                    Be the first to know who's on the panel.
                  </div>
                  <div className="inline-flex items-center gap-1.5 mt-2 text-xs font-black uppercase tracking-wider text-[#25D366] group-hover:text-white">
                    Join WhatsApp Group <ArrowRight size={14} />
                  </div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* PAST WORKSHOPS GALLERY */}
      <section className="bg-[#FAFAF7] py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute top-20 right-0 w-72 h-72 bg-[#FACC15]/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-20 left-0 w-96 h-96 bg-[#0066FF]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-12 reveal">
            <div className="inline-flex items-center gap-2 mb-4">
              <Sparkles size={14} className="text-[#FACC15]" fill="#FACC15" />
              <span className="text-xs font-black tracking-[0.25em] text-[#0066FF] uppercase">From Past Workshops</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#0A1128] mb-5">
              See What You'll <span className="relative inline-block">
                <span className="relative z-10">Walk Into.</span>
                <span className="absolute inset-x-0 bottom-1 h-4 bg-[#FACC15] -z-0" />
              </span>
            </h2>
            <p className="text-lg text-[#4B5563]">
              Real instructors. Real conversations. Real community. These are moments from previous SSU events — the vibe you're signing up for.
            </p>
          </div>

          <div className="grid grid-cols-12 gap-3 md:gap-4 reveal">
            <div className="col-span-12 md:col-span-8 md:row-span-2 relative aspect-[16/10] md:aspect-auto rounded-2xl overflow-hidden group">
              <img src="/photos/pool-demo.jpeg" alt="In-pool teaching demo with full audience watching" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/85 via-[#0A1128]/10 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#FACC15] text-[#0A1128] rounded-full mb-3">
                  <Star size={10} fill="#0A1128" />
                  <span className="text-[10px] font-black uppercase tracking-widest">Live · In-Pool Demo</span>
                </div>
                <div className="font-display text-xl md:text-3xl text-white leading-tight max-w-md">All eyes on the water. Real teaching, in real time.</div>
              </div>
            </div>

            <div className="col-span-6 md:col-span-4 relative aspect-square rounded-2xl overflow-hidden group">
              <video
                src="/photos/underwater-clip.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/75 via-transparent to-transparent pointer-events-none" />
              <div className="absolute top-3 right-3">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#FACC15] text-[#0A1128] rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0A1128] animate-pulse" />
                  <span className="text-[9px] font-black uppercase tracking-widest">Live · Underwater</span>
                </div>
              </div>
              <div className="absolute bottom-3 left-4 right-4 text-white pointer-events-none">
                <div className="text-[10px] font-black uppercase tracking-widest text-[#FACC15] mb-1">Real Footage</div>
                <div className="font-display-tight text-sm">From below the surface.</div>
              </div>
            </div>

            <div className="col-span-6 md:col-span-4 relative aspect-square rounded-2xl overflow-hidden group">
              <img src="/photos/founder-mic.jpeg" alt="SSU founder Jacob Siegel presenting" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/70 via-transparent to-transparent" />
              <div className="absolute bottom-3 left-4 right-4 text-white">
                <div className="text-[10px] font-black uppercase tracking-widest text-[#FACC15] mb-1">Founder</div>
                <div className="font-display-tight text-sm">Jacob Siegel.</div>
              </div>
            </div>

            <div className="col-span-6 md:col-span-4 relative aspect-[4/3] rounded-2xl overflow-hidden group">
              <img src="/photos/outdoor-session.jpeg" alt="Outdoor circle session at SSU workshop" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-5 right-5 text-white">
                <div className="text-[10px] font-black uppercase tracking-widest text-[#FACC15] mb-1">Day 1</div>
                <div className="font-display-tight text-base md:text-lg">Welcome circle.</div>
              </div>
            </div>

            <div className="col-span-6 md:col-span-4 relative aspect-[4/3] rounded-2xl overflow-hidden group">
              <img src="/photos/underwater-group.jpeg" alt="Underwater group photo" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-5 right-5 text-white">
                <div className="text-[10px] font-black uppercase tracking-widest text-[#FACC15] mb-1">All In</div>
                <div className="font-display-tight text-base md:text-lg">Literally.</div>
              </div>
            </div>

            <div className="col-span-12 md:col-span-4 relative aspect-[4/3] rounded-2xl overflow-hidden group">
              <img src="/photos/indoor-venue.jpeg" alt="Screened pool venue with audience watching demo" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-5 right-5 text-white">
                <div className="text-[10px] font-black uppercase tracking-widest text-[#FACC15] mb-1">The Venue</div>
                <div className="font-display-tight text-base md:text-lg">Vacation-style. Private.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-white border-y border-[#E5E7EB] py-20 lg:py-32 relative overflow-hidden">
        <div className="absolute top-10 left-0 w-72 h-72 bg-[#FACC15]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-20 right-0 w-96 h-96 bg-[#0066FF]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-12 reveal">
            <div className="inline-flex items-center gap-2 mb-4">
              <Star size={14} className="text-[#FACC15]" fill="#FACC15" />
              <span className="text-xs font-black tracking-[0.25em] text-[#0066FF] uppercase">Real Reviews · Last Retreat</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#0A1128] mb-5">
              Straight From the <span className="relative inline-block">
                <span className="relative z-10">Group Chat.</span>
                <span className="absolute inset-x-0 bottom-1 h-4 bg-[#FACC15] -z-0" />
              </span>
            </h2>
            <p className="text-lg text-[#4B5563]">
              Real messages, real instructors. Unedited words sent in the SSU group chat after last retreat — no marketing department in sight.
            </p>
          </div>

          {/* Masonry-style testimonials */}
          <div className="columns-1 md:columns-2 lg:columns-3 gap-5 [&>*]:mb-5 [&>*]:break-inside-avoid reveal">

            <figure className="bg-[#FAFAF7] border-2 border-[#E5E7EB] rounded-2xl p-7 hover:border-[#FACC15] transition-colors">
              <div className="text-[#FACC15] font-display text-4xl leading-none mb-3">"</div>
              <blockquote className="font-display-tight text-xl text-[#0A1128] leading-snug mb-5">
                I have honestly never felt so in place within such a large group before. The support, the conversations, the collaboration — it was a powerful reminder that even though this work can feel lonely, we are part of something much bigger.
              </blockquote>
              <figcaption className="flex items-center gap-3 pt-4 border-t border-[#E5E7EB]">
                <div className="w-10 h-10 rounded-full bg-[#0066FF] text-white font-display flex items-center justify-center text-sm">M</div>
                <div>
                  <div className="font-bold text-[#0A1128] text-sm">Megan Wilson</div>
                  <div className="text-xs text-[#6B7280]">Past retreat attendee</div>
                </div>
              </figcaption>
            </figure>

            <figure className="bg-[#0A1128] text-white rounded-2xl p-7 relative overflow-hidden">
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#FACC15]/20 rounded-full blur-2xl pointer-events-none" />
              <div className="text-[#FACC15] font-display text-4xl leading-none mb-3">"</div>
              <blockquote className="font-display-tight text-xl text-white leading-snug mb-5">
                Personally and professionally, this was the <span className="text-[#FACC15]">best decision</span> I could've made. I connected with so many amazing people and made incredible friendships that I know are gonna last.
              </blockquote>
              <figcaption className="flex items-center gap-3 pt-4 border-t border-white/15">
                <div className="w-10 h-10 rounded-full bg-[#FACC15] text-[#0A1128] font-display flex items-center justify-center text-sm">★</div>
                <div>
                  <div className="font-bold text-white text-sm">A past attendee</div>
                  <div className="text-xs text-white/60">After last retreat</div>
                </div>
              </figcaption>
            </figure>

            <figure className="bg-[#FAFAF7] border-2 border-[#E5E7EB] rounded-2xl p-7 hover:border-[#FACC15] transition-colors">
              <div className="text-[#FACC15] font-display text-4xl leading-none mb-3">"</div>
              <blockquote className="font-display-tight text-xl text-[#0A1128] leading-snug mb-5">
                Huge takeaway: everything is <span className="text-[#0066FF] italic">"figureoutable."</span>
              </blockquote>
              <figcaption className="flex items-center gap-3 pt-4 border-t border-[#E5E7EB]">
                <div className="w-10 h-10 rounded-full bg-[#00B4D8] text-white font-display flex items-center justify-center text-sm">M</div>
                <div>
                  <div className="font-bold text-[#0A1128] text-sm">Megan Wilson</div>
                  <div className="text-xs text-[#6B7280]">Past retreat attendee</div>
                </div>
              </figcaption>
            </figure>

            <figure className="bg-[#FAFAF7] border-2 border-[#E5E7EB] rounded-2xl p-7 hover:border-[#FACC15] transition-colors">
              <div className="text-[#FACC15] font-display text-4xl leading-none mb-3">"</div>
              <blockquote className="font-display-tight text-xl text-[#0A1128] leading-snug mb-5">
                Consistency and connection matter more than perfection. It was so inspiring to be surrounded by people who care this much about what they do — it solidified my idea that we have the best job ever.
              </blockquote>
              <figcaption className="flex items-center gap-3 pt-4 border-t border-[#E5E7EB]">
                <div className="w-10 h-10 rounded-full bg-[#A16207] text-white font-display flex items-center justify-center text-sm">C</div>
                <div>
                  <div className="font-bold text-[#0A1128] text-sm">Christina</div>
                  <div className="text-xs text-[#6B7280]">Past retreat attendee</div>
                </div>
              </figcaption>
            </figure>

            <figure className="bg-[#FAFAF7] border-2 border-[#FACC15] rounded-2xl p-7 transition-colors">
              <div className="text-[#FACC15] font-display text-4xl leading-none mb-3">"</div>
              <blockquote className="font-display-tight text-xl text-[#0A1128] leading-snug mb-5">
                No matter the obstacle, objection or challenge, you all find ways to keep moving forward. <span className="text-[#A16207]">We are a tenacious bunch.</span>
              </blockquote>
              <figcaption className="flex items-center gap-3 pt-4 border-t border-[#E5E7EB]">
                <div className="w-10 h-10 rounded-full bg-[#FACC15] text-[#0A1128] font-display flex items-center justify-center text-sm">A</div>
                <div>
                  <div className="font-bold text-[#0A1128] text-sm">Angela Wild</div>
                  <div className="text-xs text-[#6B7280]">Past retreat attendee</div>
                </div>
              </figcaption>
            </figure>

            <figure className="bg-[#FAFAF7] border-2 border-[#E5E7EB] rounded-2xl p-7 hover:border-[#FACC15] transition-colors">
              <div className="text-[#FACC15] font-display text-4xl leading-none mb-3">"</div>
              <blockquote className="font-display-tight text-xl text-[#0A1128] leading-snug mb-5">
                I learned so much and enjoyed everyone. Hard to put it into words but just know this had a real impact and I <span className="text-[#0066FF]">can't wait for the next one.</span>
              </blockquote>
              <figcaption className="flex items-center gap-3 pt-4 border-t border-[#E5E7EB]">
                <div className="w-10 h-10 rounded-full bg-[#0066FF] text-white font-display flex items-center justify-center text-sm">K</div>
                <div>
                  <div className="font-bold text-[#0A1128] text-sm">Kat</div>
                  <div className="text-xs text-[#6B7280]">Past retreat attendee</div>
                </div>
              </figcaption>
            </figure>

          </div>

          <p className="text-center mt-10 text-sm text-[#6B7280] reveal">
            Not paid. Not staged. <span className="text-[#0A1128] font-bold">Just instructors who showed up — and want to come back.</span>
          </p>
        </div>
      </section>

      <Footer />
      <MobileStickyCta tier="day-pass" />
    </div>
  );
}
